// import: styles
import { Box, Dialog } from '@mui/material';
import { submitPromptForReviewPopUpStyles } from 'apps/studio/studio-tab/components/feed-prompts-modal/submit-prompt-for-review-pop-up/SubmitPromptForReviewPopUpStyles';

// import: assets
import * as processingAnimationData from 'core/assets/lottie/green-loader-animation.json';
import * as errorAnimationData from 'core/assets/lottie/payment-paylink-error.json';
import * as successAnimationData from 'core/assets/lottie/payment-success.json';
import BaseButton from 'core/components/button/BaseButton';
import BaseTypography from 'core/components/typography/BaseTypography';

// import: config
// import: constants
import { ApiStage } from 'core/constants/enums/ApiStages.ts';

// import: components
import LottieAnimation from 'core/layouts/components/lottie-animation/LottieAnimation';
import { PROFILE } from 'core/navigation/Routes';

// import: utils
// import: services
// import: redux
import {
	publishPromptAPIStageSelector,
	setPublishPromptAPIStage,
	toggleFeedPromptsModal,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// import: lazy load components

function SubmitPromptForReviewPopUp() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const userProfile = useSelector(userProfileSelector);

	const publishPromptAPIStage = useSelector(publishPromptAPIStageSelector);

	const open = useMemo(
		() =>
			publishPromptAPIStage === ApiStage.LOADING ||
			publishPromptAPIStage === ApiStage.SUCCESS ||
			publishPromptAPIStage === ApiStage.ERROR,
		[publishPromptAPIStage],
	);

	const onClose = () => {
		if (publishPromptAPIStage === ApiStage.SUCCESS || publishPromptAPIStage === ApiStage.ERROR) {
			dispatch(setPublishPromptAPIStage(ApiStage.IDLE));
		}
	};

	//  redirect ro profile
	useEffect(() => {
		if (publishPromptAPIStage === ApiStage.SUCCESS) {
			const isAuthenticated = true;
			navigate(`/${PROFILE}/${userProfile.handle}`, {
				state: { isAuthenticated },
			});
			dispatch(toggleFeedPromptsModal({ openModal: false }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [publishPromptAPIStage]);

	const lottieComp = useMemo(() => {
		if (publishPromptAPIStage === ApiStage.SUCCESS) {
			return (
				<Box className="lottie-cont" sx={{ width: '87px' }}>
					<LottieAnimation animationData={successAnimationData} loop={false} />
				</Box>
			);
		}
		if (publishPromptAPIStage === ApiStage.ERROR) {
			return (
				<Box className="lottie-cont" sx={{ width: '87px' }}>
					<LottieAnimation animationData={errorAnimationData} loop={false} />
				</Box>
			);
		}
		return (
			<Box className="lottie-cont" sx={{ width: '174px' }}>
				<LottieAnimation animationData={processingAnimationData} />
			</Box>
		);
	}, [publishPromptAPIStage]);

	const config = useMemo(() => {
		if (publishPromptAPIStage === ApiStage.SUCCESS) {
			return {
				title: 'Submitted for review',
				subTitle:
					'After the review process is completed, the submitted prompt will be visible on your Public Profile',
			};
		}
		if (publishPromptAPIStage === ApiStage.ERROR) {
			return {
				title: 'Submission unsuccessful',
				subTitle: 'Something went wrong. Please try again',
			};
		}
		return {
			title: 'Submitting for review',
			subTitle:
				'After the review process is completed, the submitted prompt will be visible on your Public Profile',
		};
	}, [publishPromptAPIStage]);

	return (
		<Dialog
			id="submitting-prompt-for-review-dialog"
			open={open}
			aria-labelledby="submitting-prompt-for-review"
			onClose={onClose}
			disableEscapeKeyDown={
				publishPromptAPIStage === ApiStage.SUCCESS || publishPromptAPIStage === ApiStage.ERROR
			}
			sx={submitPromptForReviewPopUpStyles.dialog}
		>
			<Box
				sx={[
					submitPromptForReviewPopUpStyles.popUp,
					submitPromptForReviewPopUpStyles.submitPromptForReviewPopUp,
				]}
			>
				{lottieComp}
				<Box sx={submitPromptForReviewPopUpStyles.textFlexCol}>
					<BaseTypography variant="t1_medium" styles={{ color: 'colors.light1' }}>
						{config.title}
					</BaseTypography>
					<BaseTypography variant="body1" styles={{ color: 'colors.light3' }}>
						{config.subTitle}
					</BaseTypography>
				</Box>
				<BaseButton
					id="submit-prompt-for-review-okay-button"
					styles={submitPromptForReviewPopUpStyles.okayButton}
					disabled={publishPromptAPIStage === ApiStage.LOADING}
					onClick={onClose}
				>
					<BaseTypography variant="b1_bold" styles={{ color: 'inherit' }}>
						Okay
					</BaseTypography>
				</BaseButton>
			</Box>
		</Dialog>
	);
}

export default SubmitPromptForReviewPopUp;
